import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from "@/lib/i18n";
import { refundPolicyTranslations } from "@/lib/i18n/refundPolicy";
import { DOCUMENT_VERSIONS } from "@/lib/constants/documentVersions";

const formatVersionDate = (version: string, language: string): string => {
  const locale = language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US';
  try {
    return new Date(version).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return version;
  }
};

const RefundPolicy = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);

  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (savedLanguage && ['en', 'de', 'fr', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage as SupportedLanguage);
    } else {
      const browserLang = detectBrowserLanguage();
      setLanguage(browserLang);
      localStorage.setItem(LANGUAGE_COOKIE_NAME, browserLang);
    }
  }, []);
  
  const t = refundPolicyTranslations[language];
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-16">
          <div className="flex flex-wrap items-center justify-between mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t.breadcrumb}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="mt-2 sm:mt-0">
              <LanguageSelector language={language} onChange={setLanguage} />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-6">{t.title}</h1>
          
          <div className="prose prose-slate max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.introduction.title}</h2>
              <p className="mb-4">{t.introduction.content}</p>
            </section>

            {/* Payment Processor / MoR */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.paymentProcessor.title}</h2>
              <p className="mb-4">{t.paymentProcessor.content}</p>
            </section>

            {/* Withdrawal Right */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.withdrawalRight.title}</h2>
              <p className="mb-4">
                {t.withdrawalRight.content}{" "}
                <Link 
                  to="/cancellation-policy" 
                  className="text-primary hover:underline font-medium"
                >
                  {t.withdrawalRight.linkText}
                </Link>.
              </p>
            </section>

            {/* Digital Content Waiver - Highlighted Card */}
            <section>
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-lg">{t.digitalContentWaiver.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{t.digitalContentWaiver.content}</p>
                </CardContent>
              </Card>
            </section>

            {/* When Refunds Are Available */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.refundsAvailable.title}</h2>
              <p className="mb-4">{t.refundsAvailable.intro}</p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                {t.refundsAvailable.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* When Refunds Are NOT Available */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.refundsNotAvailable.title}</h2>
              <p className="mb-4">{t.refundsNotAvailable.intro}</p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                {t.refundsNotAvailable.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* How to Request a Refund */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.howToRequest.title}</h2>
              <p className="mb-4">{t.howToRequest.content}</p>
              <div className="bg-muted/50 rounded-lg p-4 mb-4 space-y-2">
                <p>
                  <span className="font-medium">{t.howToRequest.paddleLabel}:</span>{" "}
                  <a
                    href={t.howToRequest.paddleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {t.howToRequest.paddleUrl}
                  </a>
                </p>
                <p>
                  <span className="font-medium">{t.howToRequest.emailLabel}:</span>{" "}
                  <a
                    href={`mailto:${t.howToRequest.email}`}
                    className="text-primary hover:underline"
                  >
                    {t.howToRequest.email}
                  </a>
                </p>
              </div>
              <p className="text-sm text-muted-foreground">{t.howToRequest.noteContent}</p>
            </section>

            {/* Processing Times */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.processingTimes.title}</h2>
              <p className="mb-4">{t.processingTimes.intro}</p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">
                {t.processingTimes.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground">{t.processingTimes.note}</p>
            </section>

            {/* Chargeback Notice */}
            <section>
              <Card className="border-destructive/20 bg-destructive/5">
                <CardHeader>
                  <CardTitle className="text-lg">{t.chargebackNotice.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{t.chargebackNotice.content}</p>
                </CardContent>
              </Card>
            </section>
            
            <p className="text-sm text-muted-foreground mt-8">
              {t.lastUpdatedLabel}: {formatVersionDate(DOCUMENT_VERSIONS.refundPolicy, language)}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RefundPolicy;
